import React, { useCallback, useEffect, useState } from 'react';
import itiSecteurActiviteService from '../../services/itiSecteurActiviteService';
import ItiReferentielAdminPanel, { emptyCategoryForm } from './ItiReferentielAdminPanel';
import useAppConfirm from '../../hooks/useAppConfirm';
import { notify } from '../common/AppToast';

const emptyOptionForm = { libelle: '', sort_order: 0 };

const ItiSecteursActiviteAdminSection = () => {
  const { ask, modal } = useAppConfirm();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [optionForms, setOptionForms] = useState({});
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await itiSecteurActiviteService.getAdminTree();
      setCategories(data);
      setExpanded((prev) => {
        const next = { ...prev };
        data.forEach((cat) => {
          if (next[cat.id] === undefined) next[cat.id] = true;
        });
        return next;
      });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    if (!categoryForm.libelle.trim()) return;

    setSaving(true);
    try {
      await itiSecteurActiviteService.createCategory({
        libelle: categoryForm.libelle.trim(),
        icone: categoryForm.icone.trim() || null,
        sort_order: Number(categoryForm.sort_order) || 0,
      });
      setCategoryForm(emptyCategoryForm);
      setShowCategoryForm(false);
      notify.success('Catégorie ajoutée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async (category, patch) => {
    setSaving(true);
    try {
      await itiSecteurActiviteService.updateCategory(category.id, patch);
      notify.success(patch.active === false ? 'Catégorie masquée' : patch.active === true ? 'Catégorie visible' : 'Catégorie mise à jour');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const ok = await ask({
      title: 'Supprimer cette catégorie ?',
      message: `Supprimer la catégorie « ${category.libelle} » et toutes ses options ?`,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }

    setSaving(true);
    try {
      await itiSecteurActiviteService.deleteCategory(category.id);
      notify.success('Catégorie supprimée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOption = async (event, categoryId) => {
    event.preventDefault();
    const form = optionForms[categoryId] ?? emptyOptionForm;
    if (!form.libelle.trim()) return;

    setSaving(true);
    try {
      await itiSecteurActiviteService.createOption({
        categorie_id: categoryId,
        libelle: form.libelle.trim(),
        sort_order: Number(form.sort_order) || 0,
      });
      setOptionForms((prev) => ({ ...prev, [categoryId]: emptyOptionForm }));
      notify.success('Option ajoutée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOption = async (option, patch) => {
    setSaving(true);
    try {
      await itiSecteurActiviteService.updateOption(option.id, patch);
      notify.success(patch.active === false ? 'Option masquée' : patch.active === true ? 'Option visible' : 'Option mise à jour');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOption = async (option) => {
    const ok = await ask({
      title: 'Supprimer cette option ?',
      message: `Supprimer « ${option.libelle} » ?`,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }

    setSaving(true);
    try {
      await itiSecteurActiviteService.deleteOption(option.id);
      notify.success('Option supprimée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <ItiReferentielAdminPanel
      addOptionLabel="Ajouter un secteur"
      emptyOptionMessage="Aucun secteur pour le moment. Utilisez le champ ci-dessus pour en ajouter un."
      optionCountSingular="secteur"
      optionCountPlural="secteurs"
      optionPlaceholder="Ex. Plomberie / Chauffage"
      categoryPlaceholder="Ex. Habitat, bâtiment & travaux"
      categories={categories}
      loading={loading}
      saving={saving}
      expanded={expanded}
      onToggleCategory={(id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
      showCategoryForm={showCategoryForm}
      onToggleCategoryForm={() => setShowCategoryForm((prev) => !prev)}
      categoryForm={categoryForm}
      onCategoryFormChange={setCategoryForm}
      onCreateCategory={handleCreateCategory}
      optionForms={optionForms}
      onOptionFormChange={(categoryId, form) => setOptionForms((prev) => ({ ...prev, [categoryId]: form }))}
      onCreateOption={handleCreateOption}
      onUpdateCategory={handleUpdateCategory}
      onDeleteCategory={handleDeleteCategory}
      onUpdateOption={handleUpdateOption}
      onDeleteOption={handleDeleteOption}
    />
    {modal}
    </>
  );
};

export default ItiSecteursActiviteAdminSection;
